import { useState, useEffect } from 'react';
import axios from 'axios';
import { Leaf, Check } from 'lucide-react';
import Layout from '@/components/layout/Layout';
import BookingZone from '@/components/BookingZone';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EntretienPage = () => {
  const [pageSettings, setPageSettings] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const pageRes = await axios.get(`${API}/pages/entretien`);
        setPageSettings(pageRes.data);
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const benefits = [
    "Tonte de pelouse régulière",
    "Taille de haies et arbustes",
    "Désherbage et entretien des massifs",
    "Débroussaillage",
    "Ramassage des feuilles",
    "50% de crédit d'impôt immédiat",
    "Équipe qualifiée et équipée"
  ];

  if (loading) {
    return (
      <Layout>
        <div className="min-h-screen flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-emerald-600 border-t-transparent"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      {/* Hero */}
      <section className="bg-gradient-to-b from-emerald-50 to-white py-12 lg:py-20 pt-28 lg:pt-36" data-testid="entretien-hero">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto text-center">
            <span className="inline-block bg-emerald-100 text-emerald-700 text-sm font-medium px-4 py-2 rounded-full mb-6">
              <Leaf className="w-4 h-4 inline mr-2" />
              Entretien de jardin
            </span>
            <h1 className="font-heading font-bold text-3xl md:text-4xl lg:text-5xl text-stone-900 mb-4">
              {pageSettings?.title || "Entretien de jardin"}
            </h1>
            <p className="text-stone-600 text-lg">
              {pageSettings?.description || "Confiez l'entretien régulier de votre jardin à nos professionnels. Bénéficiez de 50% de crédit d'impôt immédiat."}
            </p>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-8 lg:py-16" data-testid="entretien-content">
        <div className="container-custom">
          <div className="flex flex-col lg:grid lg:grid-cols-2 gap-8 lg:gap-12">
            
            {/* Booking Zone - First on mobile */}
            <div className="lg:order-2 bg-white rounded-3xl shadow-xl border border-stone-100 p-4 sm:p-6 lg:p-8">
              <h2 className="font-heading font-bold text-xl sm:text-2xl text-stone-900 mb-4 sm:mb-6 text-center">
                Réserver un entretien
              </h2>
              <div className="iframe-container">
                <BookingZone 
                  embedHtml={pageSettings?.embed_html} 
                  placeholder="Le formulaire de réservation sera configuré dans le back-office"
                />
              </div>
            </div>

            {/* Benefits - Second on mobile */}
            <div className="lg:order-1">
              <h2 className="font-heading font-bold text-2xl text-stone-900 mb-6">
                Nos services d'entretien
              </h2>
              
              <div className="bg-emerald-50 rounded-2xl p-6 mb-8">
                <p className="text-emerald-800 font-semibold mb-2">Avantage fiscal</p>
                <p className="text-emerald-600 font-bold text-2xl">50% de crédit d'impôt</p>
                <p className="text-stone-600 text-sm mt-2">
                  Avance immédiate - Vous ne payez que la moitié !
                </p>
              </div>

              <ul className="space-y-4">
                {benefits.map((benefit, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <div className="flex-shrink-0 w-6 h-6 bg-emerald-100 rounded-full flex items-center justify-center mt-0.5">
                      <Check className="w-4 h-4 text-emerald-600" />
                    </div>
                    <span className="text-stone-700">{benefit}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-12 bg-emerald-600" data-testid="entretien-cta">
        <div className="container-custom text-center">
          <h2 className="font-heading font-bold text-2xl text-white mb-4">
            Des questions ?
          </h2>
          <p className="text-emerald-100 mb-6">
            Notre équipe est à votre disposition.
          </p>
          <a 
            href="tel:0465011022" 
            className="inline-flex items-center gap-2 bg-white text-emerald-600 font-semibold px-8 py-4 rounded-full hover:bg-stone-100 transition-colors text-lg"
          >
            04 65 01 10 22
          </a>
        </div>
      </section>
    </Layout>
  );
};

export default EntretienPage;
